import { ChangeDetectionStrategy, Component, inject, input, output, SecurityContext } from '@angular/core';
import { RouterLink } from '@angular/router';
import { DomSanitizer } from '@angular/platform-browser';
import { BreadcrumbComponent } from '@presentation/components/breadcrumb';
import { ChipComponent, type ChipColorValue } from '@presentation/components/chip';
import { HeadingComponent } from '@presentation/components/heading';
import { IconComponent, type IconColor, type IconSize } from '@presentation/components/icon';
import { type HeadingLevel } from '@presentation/directives';

export interface SubHeaderChip {
  readonly label: string;
  readonly color?: ChipColorValue;
  readonly backgroundColor?: string;
  readonly textColor?: string;
  readonly ariaLabel: string;
  readonly role?: string;
  /** Optional test id (`aut-id`) for the rendered chip. */
  readonly autId?: string;
}

@Component({
  selector: 'cm-sub-header-layout',
  imports: [BreadcrumbComponent, HeadingComponent, IconComponent, RouterLink, ChipComponent],
  templateUrl: './sub-header.layout.html',
  styleUrl: './sub-header.layout.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SubHeaderLayout {
  private readonly _sanitizer = inject(DomSanitizer);

  public readonly topTitle = input<string>('');
  public readonly topTitleAutId = input<string>('');
  public readonly title = input<string>('');
  public readonly titleLevel = input<HeadingLevel>('h1');
  public readonly visualTitleLevel = input<HeadingLevel>();
  public readonly subtitle = input<string>('');
  public readonly subtitleHtml = input<string>('');
  public readonly chipsPosition = input<'inline' | 'bottom'>('inline');

  public get safeSubtitleHtml(): string {
    return this._sanitizer.sanitize(SecurityContext.HTML, this.subtitleHtml()) ?? '';
  }

  public readonly iconTitle = input<string>('');
  public readonly iconColor = input<IconColor>();
  public readonly iconSize = input<IconSize>('lg');
  public readonly chips = input<SubHeaderChip[]>([]);
  public readonly subtitleChips = input<SubHeaderChip[]>([]);
  public readonly hasBackButton = input<boolean>(false);
  public readonly customBackHandler = input<boolean>(false);
  public readonly backButtonClick = output<void>();

  /**
   * Opt-in breadcrumb trail. Off by default — enable only on deeply-nested
   * pages (e.g. create/edit forms) where "where am I / how to go up" adds value.
   * The trail itself still comes from the routes' `data.breadcrumb` (WCAG 2.4.5).
   */
  public readonly showBreadcrumb = input<boolean>(false);
}
